import { Button, Card, H5, InputText } from "@sellar-io/components";
import { useState } from "react";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { VOLUME_DISCOUNT } from "../../constants/routes";
import { twMerge } from "tailwind-merge";
import { useVolumeDiscounts } from "../../hooks/api/v2/volumeDiscounts/useVolumeDiscounts";
import { useVolumeDiscountsUpdate } from "../../hooks/api/v2/volumeDiscounts/useVolumeDiscountsUpdate";
import { Icon } from "../../components";
import { capitalize } from "@sellar-io/common";
import { VolumeDiscountPriceListAssignDialog } from "./components/VolumeDiscountPriceListAssignDialog";
import { PriceListVolumeDiscountDeleteDialog } from "./components/PriceListVolumeDiscountDeleteDialog";
import { VolumeDiscountAdvancedSetupDialog } from "./components/VolumeDiscountModeDialogs";

export const VolumeDiscountDetail = () => {
  const volumeDiscountId = useParamAsNumber(
    VOLUME_DISCOUNT,
    "volumeDiscountId",
  );

  const [editedName, setEditedName] = useState<string | null>(null);
  const [addPriceListActive, setAddPriceListActive] = useState(false);
  const [advancedSetupDialogActive, setAdvancedSetupDialogActive] =
    useState(false);
  const [deletingAssignment, setDeletingAssignment] = useState<{
    priceTierVolumeDiscountsId: number;
    priceListId: number;
    priceListName: string;
  } | null>(null);

  const { data: volumeDiscount, isFetched } =
    useVolumeDiscounts(volumeDiscountId);

  const volumeDiscountUpdate = useVolumeDiscountsUpdate(volumeDiscountId);

  // Note: We'll need to fetch assigned price tiers through a different approach
  // For now, we'll show this in the UI when the backend provides the data
  const flatAssignedPriceTiers = volumeDiscount?.PriceTiers || [];

  const name = editedName ?? volumeDiscount?.name ?? "";
  const showNameSave =
    editedName !== null &&
    editedName !== volumeDiscount?.name &&
    editedName.length > 0;

  const volumeDiscountProcessing = volumeDiscountUpdate.isPending;

  return (
    <div className="flex flex-col gap-4 p-4">
      <form
        className="mx-auto flex w-full max-w-230 flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name)
            volumeDiscountUpdate.mutate(
              { name },
              {
                onSuccess: () => setEditedName(null),
              },
            );
        }}
      >
        <InputLabel>Name</InputLabel>
        <div className="flex">
          <InputContainer
            className={twMerge(
              "relative flex w-full items-center gap-1",
              showNameSave && "rounded-r-none",
            )}
          >
            <InputText
              value={name}
              onChange={(e) => setEditedName(e.target.value)}
            />
          </InputContainer>
          {showNameSave && (
            <Button
              type="submit"
              size="small"
              className="rounded-l-none px-4"
              loading={volumeDiscountProcessing}
              disabled={volumeDiscountProcessing || !name}
            >
              Update
            </Button>
          )}
        </div>
      </form>

      {isFetched && flatAssignedPriceTiers.length > 0 && (
        <div className="flex flex-col gap-2">
          <InputLabel>Apply to price lists</InputLabel>
          {flatAssignedPriceTiers.map((assignment) => (
            <Card
              key={assignment.id}
              className="flex items-center justify-between gap-3 overflow-hidden rounded-lg border border-solid bg-white p-3 hover:border-zinc-700"
            >
              <div className="flex flex-col gap-1">
                <span className="font-semibold">{assignment.name}</span>
                {assignment.fulfillmentType && (
                  <span className="text-sm text-zinc-500">
                    {capitalize(assignment.fulfillmentType)} delivery
                  </span>
                )}
              </div>
              <Button
                color="transparent"
                size="small"
                onClick={() =>
                  setDeletingAssignment({
                    priceTierVolumeDiscountsId:
                      assignment.priceTierVolumeDiscountsId,
                    priceListId: assignment.id,
                    priceListName: assignment.name,
                  })
                }
              >
                <Icon icon="CLOSE" size={0.8} />
              </Button>
            </Card>
          ))}
          <Button onClick={() => setAddPriceListActive(true)}>Add</Button>
        </div>
      )}

      {isFetched && flatAssignedPriceTiers.length === 0 && (
        <div className="flex flex-col gap-2">
          <H5 className="text-zinc-500">Not assigned to any price lists</H5>
          <Button onClick={() => setAddPriceListActive(true)}>Add</Button>
        </div>
      )}

      {volumeDiscountId && (
        <VolumeDiscountPriceListAssignDialog
          active={addPriceListActive}
          setActive={setAddPriceListActive}
          volumeDiscountId={volumeDiscountId}
          onSetUpConditionalVolumeDiscounts={() =>
            setAdvancedSetupDialogActive(true)
          }
        />
      )}

      <VolumeDiscountAdvancedSetupDialog
        active={advancedSetupDialogActive}
        onDismiss={() => setAdvancedSetupDialogActive(false)}
        onEnabled={() => setAddPriceListActive(true)}
      />

      <PriceListVolumeDiscountDeleteDialog
        active={!!deletingAssignment}
        onDismiss={() => setDeletingAssignment(null)}
        priceListVolumeDiscountsId={
          deletingAssignment?.priceTierVolumeDiscountsId ?? 0
        }
        priceListId={deletingAssignment?.priceListId ?? 0}
        priceListName={deletingAssignment?.priceListName ?? ""}
      />
    </div>
  );
};
